// src/components/user/NewTicketForm.jsx

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box, Button, TextField, Typography, MenuItem, Select,
  FormControl, FormHelperText, CircularProgress, Alert,
  Stack, Dialog, AppBar, Toolbar, IconButton, Slide,
  Container, Card, CardContent, Chip,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import CloseIcon          from "@mui/icons-material/Close";
import InventoryIcon      from "@mui/icons-material/Inventory";
import LocationOnIcon     from "@mui/icons-material/LocationOn";
import ReportProblemIcon  from "@mui/icons-material/ReportProblem";
import SupportAgentIcon   from "@mui/icons-material/SupportAgent";
import ErrorOutlineIcon   from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon   from "@mui/icons-material/InfoOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useAuth }                from "../../contexts/AuthContext";
import { helpDeskService, ROLES } from "../../services/helpDeskService";
import { singleValueListService } from "../../services/singleValueListService";

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDE TRANSITION
═══════════════════════════════════════════════════════════════════════════ */

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ═══════════════════════════════════════════════════════════════════════════
   ASSET ID NORMALISER
   Always returns a string so `formData.assetId.trim()` cannot throw when
   the underlying asset stores its id as a number.
═══════════════════════════════════════════════════════════════════════════ */

const pickAssetId = (a) => {
  const raw = a?.assetId ?? a?.assetCode ?? a?.id ?? a?.assetTag ?? "";
  return raw === null || raw === undefined ? "" : String(raw);
};

/* ═══════════════════════════════════════════════════════════════════════════
   ASSET LOCATION READER
   The asset schema may use `locationId`, `location`, `locationName`, or
   `location_id`. Return whichever is present, as a string (or "").
═══════════════════════════════════════════════════════════════════════════ */

const pickAssetLocation = (a) => {
  if (!a) return "";
  const raw =
    a.locationId ??
    a.location_id ??
    a.locationName ??
    a.location ??
    "";
  return raw === null || raw === undefined ? "" : String(raw);
};

/* ═══════════════════════════════════════════════════════════════════════════
   ROLE-SPECIFIC CONFIGURATION
═══════════════════════════════════════════════════════════════════════════ */

const ROLE_CONFIG = {
  [ROLES.USER]: {
    appBarTitle:    "Create New Support Ticket",
    appBarSubtitle: "Report an issue with your assigned assets",
    submitLabel:    "Submit Ticket",
    icon:           <SupportAgentIcon />,
    accentColor:    "#2563eb",
    issueSource:    "ticket",
    assetRequiredMode: "svl",
    showLocationField: false,
  },

  [ROLES.AUDITOR]: {
    appBarTitle:    "Raise Audit Issue",
    appBarSubtitle: "Report missing, damaged or misplaced assets",
    submitLabel:    "Raise Ticket",
    icon:           <ReportProblemIcon />,
    accentColor:    "#dc2626",
    issueSource:    "audit",
    assetRequiredMode: "all",
    showLocationField: true,
  },

  [ROLES.ADMIN]: {
    appBarTitle:    "Create New Support Ticket",
    appBarSubtitle: "Report an issue on behalf of the company",
    submitLabel:    "Submit Ticket",
    icon:           <AdminPanelSettingsIcon />,
    accentColor:    "#7c3aed",
    issueSource:    "ticket",
    assetRequiredMode: "all",
    showLocationField: false,
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
   STYLED COMPONENTS
═══════════════════════════════════════════════════════════════════════════ */

const FormRow = styled(Box)(() => ({
  display:       "flex",
  gap:           "1rem",
  marginBottom:  "1.5rem",
  flexWrap:      "wrap",
  "&:last-child": { marginBottom: 0 },
}));

const FormGroup = styled(Box, {
  shouldForwardProp: (p) => p !== "hasError",
})(({ hasError }) => ({
  flex:           1,
  minWidth:       "200px",
  display:        "flex",
  flexDirection:  "column",
  gap:            "0.375rem",
  "& .MuiOutlinedInput-root": {
    background: "#fff",
    "& fieldset":             { borderColor: hasError ? "#dc2626" : "#d1d5db" },
    "&:hover fieldset":       { borderColor: hasError ? "#dc2626" : "#3b82f6" },
    "&.Mui-focused fieldset": {
      borderColor: hasError ? "#dc2626" : "#3b82f6",
      boxShadow:   hasError
        ? "0 0 0 3px rgba(220,38,38,0.12)"
        : "0 0 0 3px rgba(59,130,246,0.12)",
    },
  },
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize:      "0.8125rem",
  fontWeight:    600,
  color:         "#374151",
  textTransform: "uppercase",
  letterSpacing: "0.025em",
}));

const RequiredMark = styled("span")(() => ({
  color:       "#ef4444",
  marginLeft:  "0.125rem",
}));

const ErrorMessage = styled(FormHelperText)(() => ({
  color:      "#dc2626",
  fontSize:   "0.75rem",
  fontWeight: 500,
  margin:     0,
}));

const cancelBtnSx = {
  px: 3, py: 1,
  backgroundColor: "#f3f4f6",
  color:           "#374151",
  border:          "1px solid #d1d5db",
  borderRadius:    "6px",
  fontWeight:      500,
  textTransform:   "none",
  "&:hover":    { backgroundColor: "#e5e7eb" },
  "&:disabled": { opacity: 0.6 },
};

const submitBtnSxBase = {
  px: 3, py: 1,
  borderRadius:  "6px",
  fontWeight:    600,
  textTransform: "none",
  minWidth:      "160px",
  display:       "flex",
  alignItems:    "center",
  gap:           1,
  color:         "#fff",
  "&:disabled":  { opacity: 0.6 },
};

/* ═══════════════════════════════════════════════════════════════════════════
   SAFE LOCALSTORAGE READER
═══════════════════════════════════════════════════════════════════════════ */

const readStoredUser = () => {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem("userData");
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════════════════════ */

const NewTicketForm = ({
  open,
  onClose,
  onSuccess,
  role         = ROLES.USER,
  auditEvent   = null,
  initialAsset = null,
  lockAsset    = false,
}) => {
  const { currentUser } = useAuth();
  const config = ROLE_CONFIG[role] || ROLE_CONFIG[ROLES.USER];

  // Resolve robust User ID and Company ID (falls back to localStorage if Context fails)
  const storedUser = readStoredUser();
  const resolvedUserId = currentUser?.uid || currentUser?.id || currentUser?.userId || storedUser?.uid || storedUser?.id;
  const resolvedCompanyId = currentUser?.companyId || storedUser?.companyId;

  /* ── SVL-driven state ────────────────────────────────────────────────── */
  const [issueTypes,         setIssueTypes]         = useState([]);
  const [assetRequiredFor,   setAssetRequiredFor]   = useState([]);
  const [priorityMapping,    setPriorityMapping]    = useState({});
  const [priorityOptions,    setPriorityOptions]    = useState([]);
  const [loadingCatalog,     setLoadingCatalog]     = useState(false);

   const buildInitialForm = useCallback((defaultIssueType = "") => ({
    issueType:        defaultIssueType,
    assetId:          pickAssetId(initialAsset),
    description:      "",
    priority:         "",
    assignedToEmail:  "",
    assignedToUid:    "",
  }), [initialAsset]);


  /* ── State ───────────────────────────────────────────────────────────── */
  const [formData,        setFormData]        = useState(() => buildInitialForm());
  const [errors,          setErrors]          = useState({});
  const [submitError,     setSubmitError]     = useState("");
  const [loading,         setLoading]         = useState(false);
  const [availableAssets, setAvailableAssets] = useState([]);
  const [loadingAssets,   setLoadingAssets]   = useState(false);
  const [receivers,       setReceivers]       = useState([]);
  const [loadingReceivers, setLoadingReceivers] = useState(false);
  const formBodyRef   = useRef(null);
  const initialized   = useRef(false);

  /* ── Derived ─────────────────────────────────────────────────────────── */

  const svlRequiresAsset = useMemo(() => {
    if (config.assetRequiredMode === "all") return true;
    if (!formData.issueType) return false;
    const needle = String(formData.issueType).trim().toLowerCase();
    return assetRequiredFor.some((v) => String(v).trim().toLowerCase() === needle);
  }, [config.assetRequiredMode, assetRequiredFor, formData.issueType]);

  const userFallbackRequiresAsset =
    role === ROLES.USER &&
    config.assetRequiredMode === "svl" &&
    assetRequiredFor.length === 0 &&
    availableAssets.length > 0;

  const requiresAsset = svlRequiresAsset || userFallbackRequiresAsset;

  const isSubmitDisabled = loading || loadingCatalog || (requiresAsset && loadingAssets);

  const hasNoAssets = !loadingAssets && availableAssets.length === 0;

  /* ── Scroll lock ─────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!open) return undefined;
    const html             = document.documentElement;
    const prevOverflow     = html.style.overflow;
    const prevPaddingRight = html.style.paddingRight;
    const scrollbarWidth   = window.innerWidth - html.clientWidth;

    html.style.overflow = "hidden";
    if (scrollbarWidth > 0) html.style.paddingRight = `${scrollbarWidth}px`;

    return () => {
      html.style.overflow     = prevOverflow;
      html.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  /* ── SVL catalog fetching ────────────────────────────────────────────── */
  const fetchCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const svlOpts = { companyId: resolvedCompanyId };

      const [typesRaw, requiredRaw, mappingRaw, prioritiesRaw] = await Promise.all([
        config.issueSource === "audit"
          ? singleValueListService.getAuditIssueTypes(svlOpts)
          : singleValueListService.getTicketIssueTypes(svlOpts),
        singleValueListService.getTicketAssetRequiredFor(svlOpts),
        singleValueListService.getTicketPriorityMapping(svlOpts),
        singleValueListService.getTicketPriorities(svlOpts),
      ]);

      const normalizedTypes = (Array.isArray(typesRaw) ? typesRaw : [])
        .map((it) => ({
          value: String(it.value ?? it.label ?? "").trim(),
          label: String(it.label ?? it.value ?? "").trim(),
        }))
        .filter((it) => it.value);

      const normalizedPriorities = (Array.isArray(prioritiesRaw) ? prioritiesRaw : [])
        .map((it) => String(it.value ?? it.label ?? "").trim())
        .filter(Boolean);

      setIssueTypes(normalizedTypes);
      setAssetRequiredFor(Array.isArray(requiredRaw) ? requiredRaw : []);
      setPriorityMapping(mappingRaw && typeof mappingRaw === "object" ? mappingRaw : {});
      setPriorityOptions(normalizedPriorities);

      setFormData((prev) => {
        const next = { ...prev };
        if (!next.issueType || !normalizedTypes.some((t) => t.value === next.issueType)) {
          next.issueType = normalizedTypes[0]?.value || "";
        }
        return next;
      });
    } catch (err) {
      console.error("[NewTicketForm] Failed to load SVL catalog:", err);
      setSubmitError(
        "Could not load ticket options. Please close and reopen the form.",
      );
    } finally {
      setLoadingCatalog(false);
    }
  }, [config.issueSource, resolvedCompanyId]);

  /* ── Asset fetching ──────────────────────────────────────────────────── */
  const fetchAssets = useCallback(async () => {
    if (!resolvedUserId || !resolvedCompanyId) return;
    setLoadingAssets(true);
    try {
      const assets = await helpDeskService.getAssetsForRole(role, {
        companyId:  resolvedCompanyId,
        userId:     resolvedUserId,
        email:      currentUser?.email || storedUser?.email,
        locationId: auditEvent?.location_id ?? auditEvent?.locationId,
      });

      setAvailableAssets((() => {
        const list = Array.isArray(assets) ? [...assets] : [];
        const initialId = pickAssetId(initialAsset);
        if (initialId && !list.some((a) => pickAssetId(a) === initialId)) {
          list.unshift(initialAsset);
        }
        return list;
      })());
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setLoadingAssets(false);
    }
  }, [
    resolvedUserId, resolvedCompanyId, role,
    auditEvent?.location_id, auditEvent?.locationId,
    initialAsset,
    currentUser?.email, storedUser?.email,
  ]);

  /* ── Open / close lifecycle ──────────────────────────────────────────── */
  useEffect(() => {
    if (!open) {
      initialized.current = false;
      return;
    }
    if (initialized.current) return;
    initialized.current = true;

    setFormData(buildInitialForm());
    setErrors({});
    setSubmitError("");
    fetchCatalog();
  }, [open, buildInitialForm, fetchCatalog]);

  // Load ticket receivers for User, Admin, AND Auditor
  useEffect(() => {
    if (!open || !resolvedCompanyId) return undefined;

    let cancelled = false;
    setLoadingReceivers(true);
    (async () => {
      try {
        const list = await helpDeskService.getAssignableUsersByReceiverRoles(
          resolvedCompanyId,
        );
        if (!cancelled) setReceivers(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("[NewTicketForm] receiver load failed:", err);
        if (!cancelled) setReceivers([]);
      } finally {
        if (!cancelled) setLoadingReceivers(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, resolvedCompanyId]);

  useEffect(() => {
    if (!open || !resolvedUserId) return;
    if (role === ROLES.USER) fetchAssets();
    else if (requiresAsset) fetchAssets();
  }, [open, requiresAsset, resolvedUserId, role, fetchAssets]);

  /* ── Priority resolution ─────────────────────────────────────────────── */
  const resolvePriority = useCallback((issueType) => {
    const key = String(issueType || "").trim();
    if (!key) return "";
    if (priorityMapping[key]) return priorityMapping[key];
    const found = Object.entries(priorityMapping).find(
      ([k]) => k.toLowerCase() === key.toLowerCase(),
    );
    return found?.[1] || "";
  }, [priorityMapping]);

  /* ── Auto-seed priority ──────────────────────────────────────────────── */
  useEffect(() => {
    setFormData((prev) => {
      if (prev.priority && priorityOptions.includes(prev.priority)) return prev;
      const mapped = resolvePriority(prev.issueType);
      if (mapped && priorityOptions.includes(mapped)) {
        return { ...prev, priority: mapped };
      }
      const fallback = priorityOptions[0] || "";
      if (prev.priority === fallback) return prev;
      return { ...prev, priority: fallback };
    });
  }, [priorityOptions, resolvePriority]);

  /* ── Form handlers ───────────────────────────────────────────────────── */
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "issueType" && config.assetRequiredMode === "svl") {
        const needle = String(value).trim().toLowerCase();
        const nowRequires = assetRequiredFor.some(
          (v) => String(v).trim().toLowerCase() === needle,
        );
        if (!nowRequires) next.assetId = "";

        const mapped = resolvePriority(value);
        const wasAuto =
          !prev.priority ||
          prev.priority === resolvePriority(prev.issueType) ||
          !priorityOptions.includes(prev.priority);
        if (wasAuto && mapped && priorityOptions.includes(mapped)) {
          next.priority = mapped;
        }
      }

      // NOTE: no `lastKnownLocation` sync here — that key is not part of the
      // model. The location shown in the UI is derived from the selected
      // asset on render (see `selectedAsset` below).

      return next;
    });
    setErrors((prev) => (prev[name] ? { ...prev, [name]: "" } : prev));
    setSubmitError("");
  }, [
    config.assetRequiredMode,
    assetRequiredFor,
    priorityOptions,
    resolvePriority,
  ]);

  /* ── Validation ──────────────────────────────────────────────────────── */
  const validate = useCallback(() => {
    const e = {};

    if (!resolvedCompanyId || !resolvedUserId) {
      setSubmitError("User session unavailable. Please refresh and try again.");
      return false;
    }

    if (!formData.issueType) {
      e.issueType = "Please select an issue type.";
    }

    if (!formData.description.trim()) {
      e.description = "Please enter a description.";
    } else if (role === ROLES.AUDITOR && formData.description.trim().length < 15) {
      e.description = "Description must be at least 15 characters.";
    }

    if (requiresAsset && !formData.assetId.trim()) {
      e.assetId = "Please select the affected asset.";
    }

     if (!formData.priority) {
      e.priority = "Please select a priority.";
    }

     
if (loadingReceivers) {
  e.assignedToEmail = "Loading ticket receivers, please wait…";
} else if (receivers.length === 0) {
  e.assignedToEmail =
    "Ticket Receivers catalog is empty. Go to Settings → Dropdown Catalogs → Ticket Receivers to add at least one user before raising tickets.";
} else if (!formData.assignedToEmail) {
  e.assignedToEmail = "Please select who should receive this ticket.";
}
    setErrors(e);

    const errorKeys = Object.keys(e);
    if (errorKeys.length > 0) {
      setSubmitError(
        `Please fix ${errorKeys.length} ${errorKeys.length === 1 ? "error" : "errors"} before submitting.`,
      );
      setTimeout(() => {
        const el = document.getElementById(`ticket-${errorKeys[0]}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    }

       return errorKeys.length === 0;
   }, [formData, role, requiresAsset, resolvedCompanyId, resolvedUserId, receivers, loadingReceivers]);

  /* ── Submit ──────────────────────────────────────────────────────────── */
  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setSubmitError("");

    try {
      const selectedAssetRecord = availableAssets.find(
        (a) => pickAssetId(a) === formData.assetId,
      );

      // FIX: `userName` is not in the model's ALLOWED_KEYS → dropped.
      // FIX: `lastKnownLocation` / `expectedLocation` were never allowed → kept out.
      // FIX: `locationId` is taken from the asset record (or audit event),
      //      not from an internal `lastKnownLocation` state field.
      const resolvedEmail = currentUser?.email || storedUser?.email || "";
       const basePayload = {
        companyId:       resolvedCompanyId,
        userId:          resolvedUserId,
        email:           resolvedEmail,
        userEmail:       resolvedEmail,
        issueType:       formData.issueType,
        assetId:         formData.assetId.trim(),
        assetName:       selectedAssetRecord?.assetName ?? "",
        description:     formData.description.trim(),
        priority:        formData.priority,
        // Assignment at raise time
        assignedToEmail: formData.assignedToEmail || null,
        assignedToUid:   formData.assignedToUid   || null,
      };
      let successMessage = "Ticket created successfully!";

      if (role === ROLES.AUDITOR) {
      const derivedLocationId =
  (
    auditEvent?.location_id ??
    auditEvent?.locationId ??
    pickAssetLocation(selectedAssetRecord)
  ) || null;

        await helpDeskService.createTicketForRole(role, {
          ...basePayload,
          auditEventId:    auditEvent?.id    ?? null,
          auditEventTitle: auditEvent?.title ?? null,
          locationId:      derivedLocationId,
        });
        successMessage = "Ticket raised successfully!";
      } else {
        await helpDeskService.createTicketForRole(role, basePayload);
      }

      setFormData(buildInitialForm(issueTypes[0]?.value || ""));
      onSuccess?.(successMessage);
      onClose?.();
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
      if (formBodyRef.current) {
        formBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
    } finally {
      setLoading(false);
    }
  }, [
    validate, availableAssets, formData, role, auditEvent, currentUser,
    storedUser, resolvedCompanyId, resolvedUserId, buildInitialForm,
    onSuccess, onClose, issueTypes,
  ]);

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  const selectedAsset =
    availableAssets.find((a) => pickAssetId(a) === formData.assetId) ?? null;

  // Derived location string for display. No storage — just for UI.
  const selectedAssetLocation =
    pickAssetLocation(selectedAsset) ||
    pickAssetLocation(initialAsset) ||
    (auditEvent?.location_name ?? "") ||
    "";

  /* ═══════════════════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════════════════ */
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={!loading ? onClose : undefined}
      TransitionComponent={Transition}
      PaperProps={{
        sx: { display: "flex", flexDirection: "column", overflow: "hidden" },
      }}
    >
      {/* ── APP BAR ── */}
      <AppBar sx={{ position: "relative", flexShrink: 0 }} elevation={1}>
        <Toolbar>
          <IconButton
            edge="start" color="inherit"
            onClick={onClose} disabled={loading}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" gap={1}>
              {config.icon && React.cloneElement(config.icon, { fontSize: "small" })}
              <Typography variant="h6" sx={{ lineHeight: 1.2 }} noWrap>
                {config.appBarTitle}
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {auditEvent
                ? `${auditEvent.title} · ${auditEvent.location_name || "—"}`
                : config.appBarSubtitle}
            </Typography>
          </Box>

          <Chip
            label={
              role === ROLES.ADMIN
                ? "Admin"
                : role === ROLES.AUDITOR
                  ? "Auditor"
                  : "User"
            }
            size="small"
            sx={{
              bgcolor:    "rgba(255,255,255,0.2)",
              color:      "#fff",
              fontWeight: 600,
              height:     22,
              fontSize:   "0.7rem",
            }}
          />
        </Toolbar>
      </AppBar>

      {/* ── BODY + FOOTER ── */}
      <Box sx={{
        display:       "flex",
        flexDirection: "column",
        bgcolor:       "#f1f5f9",
        flex:          1,
        minHeight:     0,
        overflow:      "hidden",
      }}>

        <Box
          component="form"
          id="new-ticket-form"
          ref={formBodyRef}
          onSubmit={handleSubmit}
          noValidate
          sx={{ flex: 1, overflowY: "auto", py: 4 }}
        >
          <Container maxWidth="lg">

            <Card variant="outlined" sx={{ borderRadius: 2, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
              <CardContent sx={{ p: 4 }}>

                {role === ROLES.AUDITOR && auditEvent && (
                  <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 2, mb: 4 }}>
                    <Typography variant="body2" fontWeight={700} gutterBottom>
                      Reporting for Audit
                    </Typography>
                    <Typography variant="body2">
                      <strong>{auditEvent.title}</strong>
                      {auditEvent.location_name && ` · ${auditEvent.location_name}`}
                    </Typography>
                  </Alert>
                )}

                {loadingCatalog && (
                  <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ borderRadius: 2, mb: 4 }}>
                    Loading ticket options…
                  </Alert>
                )}

                {/* 1. ISSUE TYPE + PRIORITY */}
                <FormRow>
                  <FormGroup hasError={Boolean(errors.issueType)}>
                    <FieldLabel>
                      Issue Type <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl fullWidth size="small" error={Boolean(errors.issueType)}>
                      <Select
                        id="ticket-issueType"
                        name="issueType"
                        value={formData.issueType}
                        onChange={handleChange}
                        displayEmpty
                        disabled={loadingCatalog || issueTypes.length === 0}
                      >
                        <MenuItem value="" disabled>
                          {loadingCatalog
                            ? "Loading…"
                            : issueTypes.length === 0
                              ? "No issue types configured"
                              : "Select an issue type"}
                        </MenuItem>
                        {issueTypes.map(({ value, label }) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                      </Select>
                      {errors.issueType && <ErrorMessage>{errors.issueType}</ErrorMessage>}
                    </FormControl>
                  </FormGroup>

                  <FormGroup hasError={Boolean(errors.priority)}>
                    <FieldLabel>
                      Priority <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl fullWidth size="small" error={Boolean(errors.priority)}>
                      <Select
                        id="ticket-priority"
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        displayEmpty
                        disabled={loadingCatalog || priorityOptions.length === 0}
                      >
                        <MenuItem value="" disabled>
                          {loadingCatalog
                            ? "Loading…"
                            : priorityOptions.length === 0
                              ? "No priorities configured"
                              : "Select priority"}
                        </MenuItem>
                        {priorityOptions.map((p) => (
                          <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                      </Select>
                      {errors.priority && <ErrorMessage>{errors.priority}</ErrorMessage>}
                    </FormControl>
                  </FormGroup>
                </FormRow>

                {/* 2. DESCRIPTION */}
                <FormRow>
                  <FormGroup hasError={Boolean(errors.description)}>
                    <FieldLabel>
                      Description <RequiredMark>*</RequiredMark>
                      {role === ROLES.AUDITOR && (
                        <Typography
                          component="span"
                          sx={{
                            color: "#9ca3af", fontStyle: "italic",
                            fontSize: "0.7rem", textTransform: "none", ml: 0.5,
                          }}
                        >
                          (minimum 15 characters)
                        </Typography>
                      )}
                    </FieldLabel>
                    <TextField
                      id="ticket-description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      multiline rows={4} fullWidth size="small"
                      error={Boolean(errors.description)}
                      placeholder={
                        role === ROLES.AUDITOR
                          ? "Describe the issue: When was it last seen? What condition was it in?"
                          : "Please provide detailed information about your issue…"
                      }
                      inputProps={role === ROLES.AUDITOR ? { maxLength: 500 } : {}}
                      helperText={
                        role === ROLES.AUDITOR
                          ? `${formData.description.length}/500 characters`
                          : undefined
                      }
                      FormHelperTextProps={{
                        sx: {
                          color: errors.description ? "#dc2626" : "#9ca3af",
                          fontSize: "0.75rem", fontWeight: 500,
                          margin: 0, marginTop: "4px",
                        },
                      }}
                    />
                    {errors.description && (
                      <ErrorMessage>{errors.description}</ErrorMessage>
                    )}
                  </FormGroup>
                </FormRow>

                                            {/* 2b. ASSIGN TO (User + Admin + Auditor) */}
                <FormRow>
                  <FormGroup hasError={Boolean(errors.assignedToEmail)}>
                    <FieldLabel>
                      Assign To <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <FormControl
                      fullWidth
                      size="small"
                      error={Boolean(errors.assignedToEmail)}
                      disabled={loadingReceivers || receivers.length === 0}
                    >
                      <Select
                        id="ticket-assignedToEmail"
                        name="assignedToEmail"
                        value={formData.assignedToEmail}
                        onChange={(e) => {
                          const email = e.target.value;
                          const picked =
                            receivers.find((r) => r.email === email) || null;
                          setFormData((prev) => ({
                            ...prev,
                            assignedToEmail: email,
                            assignedToUid:   picked?.uid || "",
                          }));
                          setErrors((prev) =>
                            prev.assignedToEmail
                              ? { ...prev, assignedToEmail: "" }
                              : prev,
                          );
                          setSubmitError("");
                        }}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>
                          {loadingReceivers
                            ? "Loading receivers…"
                            : receivers.length === 0
                              ? "No ticket receivers configured"
                              : "Select a receiver"}
                        </MenuItem>
                        {receivers.map((r) => (
                          <MenuItem
                            key={r.uid || r.email}
                            value={r.email}
                          >
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <SupportAgentIcon
                                sx={{ fontSize: 18, color: "#64748b" }}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight={600}>
                                  {r.name || r.email}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {r.email} · <span style={{ textTransform: 'capitalize' }}>{r.role}</span>
                                </Typography>
                              </Box>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.assignedToEmail && (
                        <ErrorMessage>{errors.assignedToEmail}</ErrorMessage>
                      )}
                      {!loadingReceivers && receivers.length === 0 && (
                        <FormHelperText sx={{ color: "#64748b", ml: 0, mt: 0.5 }}>
                          No ticket receivers configured. Contact your
                          administrator to set up the Ticket Receivers catalog.
                        </FormHelperText>
                      )}
                    </FormControl>
                  </FormGroup>
                </FormRow>

                {/* 3. AFFECTED ASSET */}
                {requiresAsset && (
                  <FormRow>
                    <FormGroup hasError={Boolean(errors.assetId)}>
                      <FieldLabel>
                        {lockAsset ? "Affected Asset" : "Select Asset"} <RequiredMark>*</RequiredMark>
                      </FieldLabel>

                      {lockAsset ? (
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <InventoryIcon sx={{ color: "#64748b" }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: "monospace" }}>
                              {formData.assetId || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {selectedAsset?.assetName || initialAsset?.assetName || "Unnamed"} · {selectedAsset?.type || initialAsset?.type || "—"}
                            </Typography>
                          </Box>
                          <Chip
                            label="Locked" size="small"
                            sx={{ bgcolor: "#e2e8f0", color: "#475569", fontWeight: 600 }}
                          />
                        </Box>
                      ) : (
                        <FormControl fullWidth size="small" error={Boolean(errors.assetId)}>
                          <Select
                            id="ticket-assetId"
                            name="assetId"
                            value={formData.assetId}
                            onChange={handleChange}
                            displayEmpty
                            disabled={loadingAssets}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
                          >
                            <MenuItem value="">
                              {loadingAssets
                                ? "Loading assets…"
                                : hasNoAssets
                                  ? role === ROLES.USER ? "No assets assigned to you" : "No assets found"
                                  : "Select an asset"}
                            </MenuItem>

                            {!loadingAssets && availableAssets.map((asset) => {
                              const aid = pickAssetId(asset);
                              return (
                                <MenuItem key={asset.id || aid} value={aid}>
                                  <Stack direction="row" alignItems="center" spacing={1.5} width="100%">
                                    <InventoryIcon sx={{ fontSize: 18, color: "#64748b" }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: "monospace" }}>
                                        {aid}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {asset.assetName || "Unnamed"} · {asset.type || "—"}
                                      </Typography>
                                    </Box>
                                  </Stack>
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {errors.assetId && <ErrorMessage>{errors.assetId}</ErrorMessage>}
                          {role === ROLES.USER && hasNoAssets && (
                            <FormHelperText sx={{ color: "#64748b", ml: 0, mt: 0.5 }}>
                              You don't have any assets assigned. Contact your administrator.
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    </FormGroup>
                  </FormRow>
                )}

                {/* 4. AUTO-POPULATED LOCATION */}
                {config.showLocationField && (
                  <FormRow>
                    <FormGroup>
                      <FieldLabel>Location</FieldLabel>
                      {selectedAssetLocation ? (
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "#eff6ff",
                            border: "1px solid #bfdbfe",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <LocationOnIcon sx={{ color: "#1d4ed8" }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={600} color="#1e3a8a">
                              {selectedAssetLocation}
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            bgcolor: "#f8fafc",
                            border: "1px dashed #cbd5e1",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <LocationOnIcon sx={{ color: "#94a3b8" }} />
                          <Typography variant="body2" color="text.secondary">
                            Location will be auto-filled based on the selected asset.
                          </Typography>
                        </Box>
                      )}
                      <FormHelperText sx={{ ml: 0, color: "#9ca3af" }}>
                        Derived automatically from the asset's record.
                      </FormHelperText>
                    </FormGroup>
                  </FormRow>
                )}

              </CardContent>
            </Card>
          </Container>
        </Box>

        {/* ── STICKY FOOTER ── */}
        <Box sx={{
          borderTop: "1px solid #e2e8f0", backgroundColor: "#fff",
          py: 2, px: 3, boxShadow: "0 -2px 8px rgba(0,0,0,0.04)", flexShrink: 0,
        }}>
          <Container maxWidth={false} disableGutters>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
              <Button type="button" onClick={onClose} disabled={loading} sx={cancelBtnSx}>
                Cancel
              </Button>
              <Button
                type="submit" form="new-ticket-form" disabled={isSubmitDisabled}
                sx={{
                  ...submitBtnSxBase,
                  backgroundColor: config.accentColor,
                  "&:hover": { backgroundColor: config.accentColor, filter: "brightness(0.9)" },
                }}
              >
                {loading && <CircularProgress size={16} color="inherit" />}
                {loading ? "Submitting…" : config.submitLabel}
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>

      {/* ── SNACKBAR ── */}
      <Snackbar
        open={Boolean(submitError)}
        autoHideDuration={6000}
        onClose={() => setSubmitError("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setSubmitError("")}
          severity="error" variant="filled" icon={<ErrorOutlineIcon />}
          sx={{
            width: "100%", minWidth: 400, maxWidth: 600,
            fontSize: "0.9rem", fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)", alignItems: "center",
          }}
        >
          {submitError}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default NewTicketForm;